import React, { useState } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Chrome, Settings, LogOut, ChevronDown } from 'lucide-react';
import { signInWithGoogle, signOutUser } from '../services/auth';
import { User } from 'firebase/auth';

const SignInButton = styled(motion.button)`
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s;
  box-shadow: 0 3px 10px rgba(245, 124, 0, 0.25);
  min-width: 180px;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(245, 124, 0, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
    min-width: 160px;
  }
`;

const SignOutButton = styled(motion.button)`
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    color: #374151;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #4b5563;
  font-size: 14px;
  font-weight: 500;
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
`;

const UserDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.9);
  padding: 6px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(245, 124, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.2s;
  cursor: pointer;
  position: relative;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
`;

const UserMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid #e2e8f0;
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
`;

const MenuItem = styled(motion.button)`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: #1e293b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s;

  &:hover {
    background: #f8fafc;
  }

  &:first-child {
    border-radius: 12px 12px 0 0;
  }

  &:last-child {
    border-radius: 0 0 12px 12px;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 4px 0;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UserName = styled.span`
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
`;

const UserEmail = styled.span`
  font-size: 12px;
  color: #64748b;
`;

interface GoogleSignInProps {
  user: User | null;
  onSignIn: (user: User) => void;
  onSignOut: () => void;
  onUserManagement?: () => void;
}

const GoogleSignIn: React.FC<GoogleSignInProps> = ({ user, onSignIn, onSignOut, onUserManagement }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleSignIn = async () => {
    try {
      const user = await signInWithGoogle();
      onSignIn(user);
    } catch (error) {
      console.error('Sign in error:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      onSignOut();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleUserManagement = () => {
    if (onUserManagement) {
      onUserManagement();
      setShowUserMenu(false);
    }
  };

  if (user) {
    return (
      <div style={{ position: 'relative' }}>
        <UserDisplay onClick={() => setShowUserMenu(!showUserMenu)}>
          <UserInfo>
            {user.photoURL ? (
              <Avatar src={user.photoURL} alt={user.displayName || 'User'} />
            ) : (
              <AvatarFallback>
                {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
              </AvatarFallback>
            )}
            <UserDetails>
              <UserName>{user.displayName}</UserName>
              <UserEmail>{user.email}</UserEmail>
            </UserDetails>
          </UserInfo>
          <ChevronDown 
            size={16} 
            style={{ 
              color: '#64748b',
              transform: showUserMenu ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }} 
          />
        </UserDisplay>
        
        <AnimatePresence>
          {showUserMenu && (
            <UserMenu
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <MenuItem
                onClick={handleUserManagement}
                whileHover={{ backgroundColor: '#f8fafc' }}
                whileTap={{ scale: 0.98 }}
              >
                <Settings size={16} />
                User Management
              </MenuItem>
              <MenuDivider />
              <MenuItem
                onClick={handleSignOut}
                whileHover={{ backgroundColor: '#f8fafc' }}
                whileTap={{ scale: 0.98 }}
              >
                <LogOut size={16} />
                Sign Out
              </MenuItem>
            </UserMenu>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <SignInButton
      onClick={handleSignIn}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Chrome />
      Sign in with Google
    </SignInButton>
  );
};

export default GoogleSignIn; 